import { ChangeSet } from "creso-core/api";
import { JOINT_CLIENT_HOLDING_PATTERNS } from "creso-core/constants";
import { useChangeSetItems } from "creso-core/hooks";
import type { Client, InvestorProfileDetails } from "creso-core/types";
import {
  capitalize,
  findPrimaryHolder,
  findSecondHolder,
  getFieldLabel,
  getValue,
  nomineeFieldOrderMap,
} from "creso-core/utils";

type Item = ChangeSet["items"][number];

// "INVESTOR_ADDRESS_INFORMATION" -> "Address Information"
const sectionTitle = (key: string) =>
  capitalize(
    key
      .replace("INVESTOR_", "")
      .replace(/_/g, " ")
      .replace(/\b\w/g, (match) => match.toUpperCase())
  );

const groupByObject = (items: Item[] = []) =>
  Object.values(
    items.reduce(
      (acc, item) => {
        acc[item.object_pk] = acc[item.object_pk] || [];
        acc[item.object_pk].push(item);
        return acc;
      },
      {} as Record<string, Item[]>
    )
  );

function FieldChange({ item }: { item: Item }) {
  return (
    <div className="flex flex-col gap-2 col-span-1">
      <span className="text-sm">{getFieldLabel(item.field_name)}</span>
      <span className="text-sm text-muted-foreground">
        {`${item.old_value ? getValue(item.field_name, item.old_value as string) : "(Empty)"} -> `}
        <span className="font-semibold text-primary">
          {item.new_value
            ? getValue(item.field_name, item.new_value as string)
            : "(Empty)"}
        </span>
      </span>
    </div>
  );
}

function FieldChangesCard({
  title,
  changes,
}: {
  title: string;
  changes: Item[];
}) {
  return (
    <div className="flex flex-col gap-3 rounded-lg bg-card border border-border p-3">
      <span className="text-sm font-semibold">{title}</span>
      <div className="grid grid-cols-2 gap-4">
        {changes.map((item) => (
          <FieldChange key={item.field_name} item={item} />
        ))}
      </div>
    </div>
  );
}

/** One card per changed bank account / nominee */
function ObjectChangesCard({
  title,
  itemLabel,
  groups,
  sortFields,
}: {
  title: string;
  itemLabel: string;
  groups: Item[][];
  sortFields?: (a: Item, b: Item) => number;
}) {
  return (
    <div className="flex flex-col rounded-lg bg-card border border-border p-3">
      <span className="text-sm font-semibold">{title}</span>
      {groups.map((items, idx) => {
        const changed = items.filter((item) => item.has_changes);
        if (!changed.length) return null;
        const sorted = sortFields ? changed.toSorted(sortFields) : changed;
        return (
          <div
            key={idx}
            className="border-b last-of-type:border-0 flex flex-col gap-3 border-border py-3"
          >
            <span className="text-sm font-medium">{`${itemLabel} ${idx + 1}`}</span>
            <div className="grid grid-cols-2 gap-4">
              {sorted.map((item) => (
                <FieldChange key={item.field_name} item={item} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Field-by-field list of a client's pending account changes (old -> new),
 * split per holder on joint accounts. Shared by the partner "notify" and
 * client "authorise" flows.
 */
export function AccountChangesSummary({
  client,
  holderProfiles,
}: {
  client: Client;
  /** Both holders' profiles, needed to split changes on joint accounts */
  holderProfiles?: InvestorProfileDetails[];
}) {
  const changes = useChangeSetItems(client.id);

  const isJointClient = Boolean(
    client.holding_pattern &&
    JOINT_CLIENT_HOLDING_PATTERNS.includes(client.holding_pattern)
  );

  if (!changes) return null;

  const primaryGuid = findPrimaryHolder(holderProfiles)?.investor_profile_guid;
  const secondGuid = findSecondHolder(holderProfiles)?.investor_profile_guid;

  // Items carrying a profile_id belong to that holder, whatever section they
  // are in. Profile items without one fall back to matching on object_pk.
  const belongsToHolder = (item: Item, holderGuid?: string) => {
    if (!isJointClient || !holderGuid) return false;
    if (item.profile_id) return item.profile_id === holderGuid;
    return item.target_model === "INVESTOR_PROFILE"
      ? item.object_pk === holderGuid
      : false;
  };
  const isHolderItem = (item: Item) =>
    belongsToHolder(item, primaryGuid) || belongsToHolder(item, secondGuid);

  const allChangedItems = Object.values(changes)
    .flat()
    .filter((item) => item.has_changes);

  const profileChanges = (changes.INVESTOR_PROFILE || []).filter(
    (item) => item.has_changes
  );
  const primaryProfileChanges = allChangedItems.filter((item) =>
    belongsToHolder(item, primaryGuid)
  );
  const secondProfileChanges = allChangedItems.filter((item) =>
    belongsToHolder(item, secondGuid)
  );

  const bankAccountChanges = groupByObject(changes.INVESTOR_BANK_ACCOUNT);
  const nomineeChanges = groupByObject(changes.NOMINEE).toSorted((a, b) => {
    if (a[0].object_pk.startsWith("temp")) return 1;
    if (b[0].object_pk.startsWith("temp")) return -1;
    return 0;
  });
  const hasChanges = (groups: Item[][]) =>
    groups.some((items) => items.some((item) => item.has_changes));

  return (
    <div className="flex flex-col gap-5">
      {isJointClient ? (
        <>
          {primaryProfileChanges.length > 0 && (
            <FieldChangesCard
              title="Primary holder profile"
              changes={primaryProfileChanges}
            />
          )}
          {secondProfileChanges.length > 0 && (
            <FieldChangesCard
              title="Second holder profile"
              changes={secondProfileChanges}
            />
          )}
        </>
      ) : (
        profileChanges.length > 0 && (
          <FieldChangesCard title="Profile" changes={profileChanges} />
        )
      )}
      {Object.entries(changes)
        .filter(
          ([key]) =>
            ![
              "INVESTMENT_ACCOUNT",
              "INVESTOR_BANK_ACCOUNT",
              "NOMINEE",
            ].includes(key) &&
            (isJointClient || key !== "INVESTOR_PROFILE")
        )
        .map(([key, items]) => {
          const changed = items.filter(
            (item) => item.has_changes && !isHolderItem(item)
          );
          if (!changed.length) return null;
          return (
            <FieldChangesCard
              key={key}
              title={sectionTitle(key)}
              changes={changed}
            />
          );
        })}
      {hasChanges(bankAccountChanges) && (
        <ObjectChangesCard
          title="Bank details"
          itemLabel="Bank account"
          groups={bankAccountChanges}
        />
      )}
      {hasChanges(nomineeChanges) && (
        <ObjectChangesCard
          title="Nominees"
          itemLabel="Nominee"
          groups={nomineeChanges}
          sortFields={(a, b) =>
            (nomineeFieldOrderMap[a.field_name] ?? 0) -
            (nomineeFieldOrderMap[b.field_name] ?? 0)
          }
        />
      )}
    </div>
  );
}
